import * as React from "react";
import { Marker } from "@react-google-maps/api";

import type { Stop } from "../../types";
import type { StopColors } from "../../utils/mapStopColors";

import { rootIdOf } from "../Map/transitMap/helpers";
import { createStopTooltipOverlay } from "./tooltips/stopTooltipOverlay";

type Props = {
  map: google.maps.Map | null;
  locationsByRoot: Record<string, Stop[]>;
  expandedRoots: Set<string>;

  activeStopId?: string | null;
  listedStopRootIds: Set<string>;

  activeLineStopRootIds?: Set<string> | null;
  activeLineColor?: string | null;

  activeZoneStopRootIds?: Set<string> | null;
  activeZoneColor?: string | null;

  colors: StopColors;
  onClickStop?: (s: Stop) => void;
};

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeXml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function getStopPointLabel(stop: Stop) {
  const markerLabel = (stop as Stop & { markerLabel?: string }).markerLabel;

  return String(markerLabel ?? stop.type ?? "")
    .replace(/[()]/g, "")
    .trim();
}

function getStopPointTitle(stop: Stop) {
  const name = String(stop.name ?? "").trim();
  const label = getStopPointLabel(stop);

  if (!label) return name;

  const alreadyHasSuffix = new RegExp(`\\(${escapeRegExp(label)}\\)\\s*$`).test(
    name
  );

  return alreadyHasSuffix ? name : `${name} (${label})`;
}

function createStopLocationIcon(
  label: string,
  active: boolean,
  selected: boolean,
  lineColor: string | null,
  colors: StopColors,
  glass: boolean,
  hovered: boolean,
  zoneColor: string | null
): google.maps.Icon {
  const size = active || hovered ? 46 : 38;

  const isNartrafik = label.trim().toLocaleUpperCase("sv-SE") === "Ä";
  const nartrafikBlue = "#0078C7";
  const nartrafikSelectedBlue = "#00558D";

  let fill: string;
  let stroke: string;
  let textColor: string;

  if (isNartrafik) {
    fill = selected && !active ? nartrafikSelectedBlue : nartrafikBlue;
    stroke = fill;
    textColor = colors.activeIcon;
  } else {
    fill = active
      ? colors.activeBg
      : selected
      ? colors.selectedBg
      : lineColor ?? colors.ghostBg;

    stroke = active
      ? colors.activeBg
      : selected
      ? colors.selectedBg
      : lineColor
      ? colors.activeIcon
      : colors.ghostBorder;

    textColor = active
      ? colors.activeIcon
      : selected
      ? colors.selectedIcon
      : lineColor
      ? colors.activeIcon
      : colors.ghostIcon;
  }

  if (!active && zoneColor) {
    fill = zoneColor;
    stroke = colors.activeIcon;
    textColor = colors.activeIcon;
  }

  if (glass) {
    stroke = isNartrafik ? nartrafikBlue : colors.ghostBorder;
    textColor = isNartrafik ? nartrafikSelectedBlue : colors.ghostIcon;
  }

  const glassDefs = glass
    ? `
      <defs>
        <linearGradient
          id="location-glass"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stop-color="#FFFFFF"
            stop-opacity="${hovered ? 1 : 0.95}"
          />
          <stop
            offset="45%"
            stop-color="#DCEAF5"
            stop-opacity="${hovered ? 0.9 : 0.65}"
          />
          <stop
            offset="100%"
            stop-color="#DCEAF5"
            stop-opacity="${hovered ? 0.75 : 0.4}"
          />
        </linearGradient>
      </defs>
    `
    : "";

  const svg = `
    <svg
      width="${size}"
      height="${size}"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
    >
      ${glassDefs}
      <path
        d="M24 3C15.16 3 8 10.16 8 19c0 11.5 16 26 16 26s16-14.5 16-26C40 10.16 32.84 3 24 3Z"
        fill="${glass ? "url(#location-glass)" : escapeXml(fill)}"
        stroke="${escapeXml(stroke)}"
        stroke-opacity="${glass ? (hovered ? 0.7 : 0.45) : 1}"
        stroke-width="${glass ? 1.5 : 3}"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <text
        x="24"
        y="24"
        text-anchor="middle"
        font-family="Arial, sans-serif"
        font-size="17"
        font-weight="700"
        fill="${escapeXml(textColor)}"
      >${escapeXml(label)}</text>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size),
  };
}

export default function StopLocationsLayer({
  map,
  locationsByRoot,
  expandedRoots,
  activeStopId,
  listedStopRootIds,
  activeLineStopRootIds,
  activeLineColor,
  activeZoneStopRootIds,
  activeZoneColor,
  colors,
  onClickStop,
}: Props) {
  const tooltipRef = React.useRef<ReturnType<
    typeof createStopTooltipOverlay
  > | null>(null);

  const [hoveredId, setHoveredId] = React.useState<string | null>(null);

  if (!tooltipRef.current) {
    tooltipRef.current = createStopTooltipOverlay();
  }

  React.useEffect(() => {
    return () => {
      tooltipRef.current?.hide();
    };
  }, []);

  const iconCache = React.useMemo(
    () => new Map<string, google.maps.Icon>(),
    [colors]
  );

  const activeId = activeStopId == null ? null : String(activeStopId);
  const activeRootId = activeId == null ? null : rootIdOf(activeId);
  const wholeStopActive = activeId != null && activeId === activeRootId;

  const hasLineFocus = Boolean(activeLineColor && activeLineStopRootIds?.size);
  const hasFocus = activeId != null || hasLineFocus;

  const markers: JSX.Element[] = [];

  for (const rootId of expandedRoots) {
    const list = locationsByRoot[rootId] ?? [];
    const seenLocationIds = new Set<string>();

    const belongsToActiveStop = rootId === activeRootId;
    const isSelected = listedStopRootIds.has(rootId) || belongsToActiveStop;

    const lineColor =
      activeLineStopRootIds?.has(rootId) && activeLineColor
        ? activeLineColor
        : null;

    for (const stop of list) {
      const id = String(stop.id);

      if (seenLocationIds.has(id)) continue;
      seenLocationIds.add(id);

      const label = getStopPointLabel(stop);
      const title = getStopPointTitle(stop);

      const isNartrafikLocation =
        label.trim().toLocaleUpperCase("sv-SE") === "Ä";

      const zoneColor =
        isNartrafikLocation && activeZoneStopRootIds?.has(rootId)
          ? activeZoneColor ?? null
          : null;

      const isActive =
        activeId != null &&
        (id === activeId || (wholeStopActive && belongsToActiveStop));

      const isHovered = hoveredId === id;

      const glass =
        hasFocus && !isActive && !isSelected && !lineColor && !zoneColor;

      const iconKey = JSON.stringify([
        label,
        isActive,
        isSelected,
        lineColor,
        glass,
        isHovered,
        zoneColor,
      ]);

      let icon = iconCache.get(iconKey);

      if (!icon) {
        icon = createStopLocationIcon(
          label,
          isActive,
          isSelected,
          lineColor,
          colors,
          glass,
          isHovered,
          zoneColor
        );

        iconCache.set(iconKey, icon);
      }

      markers.push(
        <Marker
          key={`loc-${rootId}-${id}`}
          position={{ lat: stop.lat, lng: stop.lng }}
          title={title}
          icon={icon}
          opacity={1}
          onMouseOver={() => {
            setHoveredId(id);

            if (!map) return;

            tooltipRef.current?.show(
              map,
              { lat: stop.lat, lng: stop.lng },
              title
            );
          }}
          onMouseOut={() => {
            setHoveredId(null);
            tooltipRef.current?.hide();
          }}
          onClick={() => onClickStop?.(stop)}
          zIndex={
            isHovered
              ? 2000
              : isActive
              ? 1800
              : isSelected
              ? 1400
              : zoneColor || lineColor
              ? 1300
              : 1200
          }
        />
      );
    }
  }

  return <>{markers}</>;
}
